import { Download, ExternalLink, FileCode, FileText, Trash2, Video } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";

import { AsyncBoundary, EmptyState } from "@/components/data-state";
import { DeleteDialog } from "@/components/delete-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAsync } from "@/hooks/use-async";
import { useAdminCourse, useAdminKey } from "@/hooks/use-auth";
import type { AdminReport, ReportChallenge, ReportsOverview } from "@/lib/api";
import { adminApi } from "@/lib/api";
import { formatBytes, formatTimestamp } from "@/lib/format";
import { downloadFile, formatDifficulty, formatDue, formatRun, openPdf } from "@/lib/reports";

/**
 * What each team handed in, per challenge, for the selected course -- which is also one
 * term, so switching the course above reads another semester's record.
 *
 * Two views of the same rows. "By student" is the roster against the challenges, so a
 * missing report is a visible gap next to a name; "By team" is the reports themselves,
 * with their files. CSV is for the gradebook.
 */
export function AdminReportsRoute() {
  const adminKey = useAdminKey();
  const courseId = useAdminCourse();
  const overview = useAsync(
    adminKey && courseId ? () => adminApi.reports(courseId, adminKey) : null,
    [adminKey, courseId],
  );

  return (
    <AsyncBoundary state={overview}>
      {(data) =>
        data.challenges.length === 0 ? (
          <EmptyState
            title="No challenge in this course collects a report"
            description={
              <>
                Turn it on in a challenge&apos;s{" "}
                <Link to="/admin/challenges" className="underline underline-offset-4">
                  Report section
                </Link>
                . Teams then rate the difficulty and hand in their slides and code on the
                challenge page.
              </>
            }
          />
        ) : (
          <ReportsView data={data} onChanged={overview.refetch} />
        )
      }
    </AsyncBoundary>
  );
}

function ReportsView({ data, onChanged }: { data: ReportsOverview; onChanged: () => void }) {
  const byId = useMemo(() => new Map(data.reports.map((r) => [r.id, r])), [data.reports]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">
            Reports · {data.course.code} · {data.course.term.label}
          </h2>
          <p className="text-muted-foreground text-sm">
            Staff only. Students see their own team&apos;s report and nothing else.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => downloadCsv(data)}>
          <Download className="size-4" aria-hidden />
          Export CSV
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data.challenges.map((c) => (
          <ChallengeTally key={c.id} challenge={c} data={data} />
        ))}
      </div>

      <Tabs defaultValue="students">
        <TabsList>
          <TabsTrigger value="students">By student</TabsTrigger>
          <TabsTrigger value="teams">By team</TabsTrigger>
        </TabsList>

        <TabsContent value="students" className="mt-4">
          <div className="overflow-x-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead>Team</TableHead>
                  {data.challenges.map((c) => (
                    <TableHead key={c.id}>{c.name}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.students.map((s) => (
                  <TableRow key={s.student_id}>
                    <TableCell>
                      <div className="font-medium">{s.name || s.email}</div>
                      {s.name ? (
                        <div className="text-muted-foreground text-xs">{s.email}</div>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{s.team_name ?? "—"}</TableCell>
                    {data.challenges.map((c) => {
                      const report = s.reports[c.id] ? byId.get(s.reports[c.id]!) : undefined;
                      return (
                        <TableCell key={c.id} className="max-w-72 min-w-48 align-top whitespace-normal">
                          {report ? (
                            <ReportCell report={report} />
                          ) : (
                            <span className="text-muted-foreground text-xs">Not handed in</span>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="teams" className="mt-4 space-y-6">
          {data.challenges.map((c) => (
            <TeamReports
              key={c.id}
              challenge={c}
              reports={data.reports.filter((r) => r.challenge_id === c.id)}
              onChanged={onChanged}
            />
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ChallengeTally({ challenge, data }: { challenge: ReportChallenge; data: ReportsOverview }) {
  const reports = data.reports.filter((r) => r.challenge_id === challenge.id);
  const covered = data.students.filter((s) => s.reports[challenge.id]).length;
  const late = reports.filter((r) => r.late).length;
  const meanDifficulty = reports.length
    ? reports.reduce((sum, r) => sum + r.difficulty, 0) / reports.length
    : null;
  return (
    <div className="rounded-lg border px-3.5 py-3">
      <p className="text-sm font-medium">{challenge.name}</p>
      <p className="text-muted-foreground text-xs">
        {formatDue(challenge.report_deadline)}
        {challenge.report_deadline !== null ? ` · ${formatTimestamp(challenge.report_deadline)}` : ""}
      </p>
      <p className="mt-2 text-sm">
        {reports.length} team{reports.length === 1 ? "" : "s"} handed in · {covered} of{" "}
        {data.students.length} students covered
        {late ? <span className="text-destructive"> · {late} late</span> : null}
      </p>
      {meanDifficulty !== null ? (
        <p className="text-muted-foreground text-xs">
          Mean difficulty {meanDifficulty.toFixed(1)} of 5
        </p>
      ) : null}
    </div>
  );
}

function OpenPdf({ report, label }: { report: AdminReport; label?: string }) {
  const adminKey = useAdminKey();
  return (
    <Button
      variant="outline"
      size="xs"
      onClick={() => adminKey && openPdf(() => adminApi.reportFileUrl(report.id, adminKey))}
    >
      <FileText className="size-3.5" aria-hidden />
      {label ?? "PDF"}
    </Button>
  );
}

function DownloadCode({ report, label }: { report: AdminReport; label?: string }) {
  const adminKey = useAdminKey();
  return (
    <Button
      variant="outline"
      size="xs"
      onClick={() =>
        adminKey &&
        downloadFile(() => adminApi.reportCodeUrl(report.id, adminKey), report.code_file_name)
      }
    >
      <FileCode className="size-3.5" aria-hidden />
      {label ?? "Code"}
    </Button>
  );
}

function ReportCell({ report }: { report: AdminReport }) {
  return (
    <div className="grid gap-1">
      <span className="text-sm">Difficulty {formatDifficulty(report.difficulty)}</span>
      <div className="flex flex-wrap items-center gap-1.5">
        <OpenPdf report={report} />
        <DownloadCode report={report} />
        {report.late ? <Badge variant="destructive">Late</Badge> : null}
      </div>
    </div>
  );
}

function TeamReports({
  challenge,
  reports,
  onChanged,
}: {
  challenge: ReportChallenge;
  reports: AdminReport[];
  onChanged: () => void;
}) {
  const adminKey = useAdminKey();
  const [deleting, setDeleting] = useState<AdminReport | null>(null);
  const preview = useAsync(
    deleting && adminKey ? () => adminApi.previewDeleteReport(deleting.id, adminKey) : null,
    [deleting?.id, adminKey],
  );

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">{challenge.name}</h3>
      {reports.length === 0 ? (
        <p className="text-muted-foreground text-sm">No team has handed in yet.</p>
      ) : (
        <div className="grid gap-3">
          {reports.map((r) => (
            <article key={r.id} className="rounded-lg border px-4 py-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium">
                    {r.team_name}
                    <span className="text-muted-foreground font-normal">
                      {" "}
                      · difficulty {formatDifficulty(r.difficulty)}
                    </span>
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {r.current_team_name && r.current_team_name !== r.team_name
                      ? `Now ${r.current_team_name}. `
                      : r.team_id === null
                        ? "Team since disbanded. "
                        : ""}
                    {r.members.map((m) => m.name || m.email).join(", ")}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  {r.late ? <Badge variant="destructive">Late</Badge> : null}
                  <OpenPdf report={r} label={`${r.file_name} (${formatBytes(r.file_size)})`} />
                  <DownloadCode
                    report={r}
                    label={`${r.code_file_name} (${formatBytes(r.code_file_size)})`}
                  />
                  {r.repo_url ? (
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => window.open(r.repo_url, "_blank", "noopener,noreferrer")}
                    >
                      <ExternalLink className="size-3.5" aria-hidden />
                      Repository
                    </Button>
                  ) : null}
                  {r.video_url ? (
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => window.open(r.video_url, "_blank", "noopener,noreferrer")}
                    >
                      <Video className="size-3.5" aria-hidden />
                      Video
                    </Button>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`Delete ${r.team_name}'s report`}
                    onClick={() => setDeleting(r)}
                  >
                    <Trash2 className="size-3.5" aria-hidden />
                  </Button>
                </div>
              </div>
              <p className="mt-2 text-sm">
                <span className="text-muted-foreground">Run to look at: </span>
                {r.run ? (
                  <Link
                    to={`/admin/challenges/${r.challenge_id}/attempts`}
                    className="underline underline-offset-4"
                  >
                    {formatRun(r.run)}
                  </Link>
                ) : (
                  "none named"
                )}
              </p>
              {r.problems ? (
                <p className="text-muted-foreground mt-1 text-sm whitespace-pre-wrap">
                  <span className="text-foreground">What failed: </span>
                  {r.problems}
                </p>
              ) : null}
              <p className="text-muted-foreground mt-2 text-xs">
                Handed in {formatTimestamp(r.submitted_at)}
                {r.submitted_by ? ` by ${r.submitted_by}` : ""}
                {r.revisions > 1
                  ? ` · version ${r.revisions}, first ${formatTimestamp(r.first_submitted_at)}`
                  : ""}
              </p>
            </article>
          ))}
        </div>
      )}

      <DeleteDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Delete this report?"
        description={preview.data ?? null}
        loadingDescription={preview.loading}
        confirmToken={deleting?.id ?? ""}
        onConfirm={async () => {
          if (deleting && adminKey) await adminApi.deleteReport(deleting.id, adminKey);
        }}
        onDone={() => {
          setDeleting(null);
          onChanged();
        }}
      />
    </section>
  );
}

/** One row per student, with each challenge's difficulty, time and files: for the gradebook. */
function downloadCsv(data: ReportsOverview) {
  const byId = new Map(data.reports.map((r) => [r.id, r]));
  const head = ["term", "course", "email", "name", "team"];
  for (const c of data.challenges) {
    head.push(
      `${c.name}: difficulty`,
      `${c.name}: run`,
      `${c.name}: run points`,
      `${c.name}: what failed`,
      `${c.name}: repository`,
      `${c.name}: handed in`,
      `${c.name}: late`,
      `${c.name}: report`,
      `${c.name}: code`,
    );
  }
  const rows = data.students.map((s) => {
    const row = [data.course.term.label, data.course.code, s.email, s.name, s.team_name ?? ""];
    for (const c of data.challenges) {
      const id = s.reports[c.id];
      const r = id ? byId.get(id) : undefined;
      row.push(
        r ? String(r.difficulty) : "",
        r?.run?.id ?? "",
        r?.run?.points != null ? String(r.run.points) : "",
        r?.problems ?? "",
        r?.repo_url ?? "",
        r ? formatTimestamp(r.submitted_at) : "",
        r ? (r.late ? "yes" : "no") : "",
        r?.file_name ?? "",
        r?.code_file_name ?? "",
      );
    }
    return row;
  });
  const csv = [head, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    .join("\r\n");
  const url = URL.createObjectURL(new Blob(["﻿", csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `reports-${data.course.id}.csv`;
  a.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
